/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-09 22:10:10
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-07-11 09:40:41
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { message, TablePaginationConfig } from 'antd'
import {
  RectifyRecord,
  RectifyDataState,
  PaginationState,
  FilterParams
} from '../types'

// Hook 配置选项类型
interface UseRectifyDataOptions {
  fetchRectificationList: (params: any) => Promise<any>
  updateCurrentPageSelection: (records: RectifyRecord[]) => void
  groupId: string | number | null
  userId: string | number | null
}

/**
 * 管理整改记录数据加载与筛选逻辑的Hook
 * @param {Object} options - 配置选项
 * @param {Function} options.fetchRectificationList - 获取整改记录列表的API函数
 * @param {Function} options.updateCurrentPageSelection - 更新当前页选择状态的函数
 * @param {String|Number} options.groupId - 群组ID
 * @param {String|Number} options.userId - 用户ID
 * @returns {Object} 数据相关的状态和方法
 */
const useRectifyData = ({
  fetchRectificationList,
  updateCurrentPageSelection,
  groupId,
  userId
}: UseRectifyDataOptions) => {
  // 数据和加载状态
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<RectifyDataState>({
    records: [],
    totalCount: 0,
    unRectifiedCount: 0,
    rectifiedCount: 0
  })
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [filterParams, setFilterParams] = useState<FilterParams>({})

  // 使用ref来存储最新的状态值，避免闭包问题
  const filterParamsRef = useRef(filterParams)
  const paginationRef = useRef(pagination)

  // 当状态变化时更新ref
  useEffect(() => {
    filterParamsRef.current = filterParams
  }, [filterParams])

  useEffect(() => {
    paginationRef.current = pagination
  }, [pagination])

  /**
   * 应用筛选条件
   * @param {Array} records - 原始记录
   * @param {Object} params - 筛选参数
   * @returns {Array} 筛选后的记录
   */
  const applyFilters = useCallback((records: RectifyRecord[], params: FilterParams) => {
    let filteredRecords = [...records]

    // 根据状态筛选
    if (params.status && params.status !== 0) {
      filteredRecords = filteredRecords.filter(
        (record) => record.status === params.status
      )
    }

    // 根据记录编号筛选
    if (params.recordNo) {
      filteredRecords = filteredRecords.filter(
        (record) => record.recordNo === params.recordNo
      )
    }

    // 根据创建用户筛选
    if (params.createUserIDs && params.createUserIDs.length > 0) {
      filteredRecords = filteredRecords.filter((record) =>
        record.createUserID !== undefined && params.createUserIDs!.includes(record.createUserID)
      )
    }

    // 根据整改用户筛选
    if (params.rectificationUserIDs && params.rectificationUserIDs.length > 0) {
      filteredRecords = filteredRecords.filter((record) =>
        record.rectificationUser?.some((user) =>
          params.rectificationUserIDs!.includes(user.userID)
        )
      )
    }

    return filteredRecords
  }, [])

  /**
   * 加载数据函数 - 使用useRef包装以确保引用稳定
   */
  const loadDataRef = useRef(async (params: FilterParams = {}) => {
    setLoading(true)
    try {
      // 使用ref中存储的最新值构建请求参数
      const currentFilterParams = filterParamsRef.current
      const currentPagination = paginationRef.current

      // 构建请求参数
      const requestParams = {
        ...currentFilterParams,
        ...params,
        groupID: groupId,
        userID: userId,
        page: params.page || currentPagination.current,
        pageSize: params.pageSize || currentPagination.pageSize
      }

      // 调用API获取数据
      const response = await fetchRectificationList(requestParams)

      if (response && response.status === 0) {
        const {
          records,
          totalCount,
          unRectifiedCount,
          rectifiedCount
        } = response

        // 构建新的数据对象
        const newData = {
          records: records || [],
          totalCount: totalCount || 0,
          unRectifiedCount: unRectifiedCount || 0,
          rectifiedCount: rectifiedCount || 0
        }

        // 更新数据状态
        setData(newData)

        // 更新分页状态
        setPagination({
          ...currentPagination,
          current: params.page || currentPagination.current,
          pageSize: params.pageSize || currentPagination.pageSize,
          total: totalCount || 0
        })

        // 更新当前页的选中状态
        updateCurrentPageSelection(records || [])

        // 返回最新数据
        return newData
      } else {
        message.error(response?.msg || '获取数据失败')
        return null
      }
    } catch (error) {
      console.error('获取整改记录列表失败:', error)
      message.error('获取数据失败，请稍后重试')
      return null
    } finally {
      setLoading(false)
    }
  })

  // 稳定的loadData函数，通过loadDataRef调用实际的函数
  const loadData = useCallback((params: FilterParams = {}) => {
    return loadDataRef.current(params)
  }, [])

  /**
   * 处理筛选
   * @param {Object} params - 筛选参数
   * @param {Function} resetSelection - 重置选择状态的函数
   * @param {Function} callback - 筛选完成后的回调函数
   */
  const handleFilter = useCallback(
    (params: FilterParams, resetSelection?: () => void, callback?: (data: RectifyDataState | null) => void) => {
      // 筛选条件改变时，清空选择状态
      if (resetSelection) {
        resetSelection()
      }

      setFilterParams(params)
      setPagination((prev) => ({
        ...prev,
        current: 1
      }))

      // 加载数据，完成后执行回调
      loadData({ ...params, page: 1 }).then((latestData) => {
        // 如果提供了回调，则执行回调并传递最新数据
        // 埋点用
        if (typeof callback === 'function') {
          callback(latestData)
        }
      })
    },
    [loadData]
  )

  /**
   * 处理表格分页、排序、筛选变化
   * @param {Object} pagination - 分页信息
   * @param {Object} filters - 筛选信息
   * @param {Object} sorter - 排序信息
   */
  const handleTableChange = useCallback(
    (paginationConfig: TablePaginationConfig, filters?: any, sorter?: any) => {
      loadData({
        page: paginationConfig.current,
        pageSize: paginationConfig.pageSize
      })
    },
    [loadData]
  )

  // 在组件初始化时设置ref的初始值
  useEffect(() => {
    filterParamsRef.current = filterParams
    paginationRef.current = pagination
  }, [])

  return {
    // 状态
    loading,
    data,
    pagination,
    filterParams,
    // 方法
    loadData,
    handleFilter,
    handleTableChange,
    applyFilters
  }
}

export default useRectifyData
